const STUDENT_VERIFICATION_FLOW_KEY = "studentVerificationFlow";

export const STUDENT_OTP_ATTEMPTS = 5;
export const STUDENT_OTP_EXPIRY_MS = 5 * 60 * 1000;

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Unable to persist ${key}:`, error);
  }
};

const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Unable to clear ${key}:`, error);
  }
};

const safeParse = (value, fallbackValue) => {
  if (!value) return fallbackValue;

  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallbackValue;
  }
};

const normalizeAttemptsRemaining = (value) => {
  const nextValue = Number(value);

  if (!Number.isFinite(nextValue)) {
    return STUDENT_OTP_ATTEMPTS;
  }

  return Math.max(0, Math.min(STUDENT_OTP_ATTEMPTS, Math.floor(nextValue)));
};

const normalizeTimestamp = (value) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 0;
};

export const normalizeStudentVerificationFlow = (value = {}) => ({
  email: String(value?.email || "").trim().toLowerCase(),
  preAuthToken: String(value?.preAuthToken || value?.pre_auth_token || "").trim(),
  attemptsRemaining: normalizeAttemptsRemaining(value?.attemptsRemaining),
  expiresAt: normalizeTimestamp(value?.expiresAt),
  lastOtpRequestedAt: normalizeTimestamp(value?.lastOtpRequestedAt),
  verificationType: String(value?.verificationType || "otp").trim() || "otp",
  accountType: String(value?.accountType || "student").trim() || "student",
  canResendVerification:
    value?.canResendVerification == null ? true : Boolean(value.canResendVerification),
  source: String(value?.source || "").trim(),
});

export const getStoredStudentVerificationFlow = () =>
  normalizeStudentVerificationFlow(
    safeParse(safeStorageGet(STUDENT_VERIFICATION_FLOW_KEY), {})
  );

export const persistStudentVerificationFlow = (value = {}) => {
  const nextValue = normalizeStudentVerificationFlow(value);
  safeStorageSet(STUDENT_VERIFICATION_FLOW_KEY, nextValue);
  return nextValue;
};

export const clearStudentVerificationFlow = () => {
  safeStorageRemove(STUDENT_VERIFICATION_FLOW_KEY);
};

export const createStudentVerificationFlow = (
  value = {},
  options = {}
) => {
  const now = Date.now();
  const resetCycle = options.resetCycle !== false;

  return persistStudentVerificationFlow({
    ...getStoredStudentVerificationFlow(),
    ...value,
    attemptsRemaining: resetCycle
      ? STUDENT_OTP_ATTEMPTS
      : normalizeAttemptsRemaining(
          value?.attemptsRemaining ??
            getStoredStudentVerificationFlow().attemptsRemaining
        ),
    lastOtpRequestedAt: resetCycle
      ? now
      : normalizeTimestamp(
          value?.lastOtpRequestedAt ??
            getStoredStudentVerificationFlow().lastOtpRequestedAt
        ),
    expiresAt: resetCycle
      ? now + STUDENT_OTP_EXPIRY_MS
      : normalizeTimestamp(
          value?.expiresAt ?? getStoredStudentVerificationFlow().expiresAt
        ),
  });
};

export const decrementStudentOtpAttempts = () => {
  const currentValue = getStoredStudentVerificationFlow();
  return persistStudentVerificationFlow({
    ...currentValue,
    attemptsRemaining: Math.max(0, currentValue.attemptsRemaining - 1),
  });
};

export const markStudentOtpExpired = () => {
  const currentValue = getStoredStudentVerificationFlow();
  return persistStudentVerificationFlow({
    ...currentValue,
    expiresAt: Date.now(),
  });
};

export const hasStudentOtpExpired = (value = {}, now = Date.now()) => {
  const expiresAt = normalizeTimestamp(value?.expiresAt);
  if (!expiresAt) return true;
  return expiresAt <= now;
};

export const getStudentOtpRemainingMs = (value = {}, now = Date.now()) => {
  const expiresAt = normalizeTimestamp(value?.expiresAt);
  if (!expiresAt) return 0;
  return Math.max(0, expiresAt - now);
};

export const formatStudentOtpTimer = (remainingMs = 0) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};
